/**
 * <bracelet-builder> — Build Your Own Bracelet.
 *
 * Reads the bead catalogue and config that sections/bracelet-builder.liquid
 * renders as JSON and lets the shopper fill N bead slots (N = the size's
 * bead_count). Each finished bracelet is ONE cart line on its size variant, with
 * the design as line-item properties:
 *   Name           the name in alphabet beads, if any
 *   Beads          human readable  "Amethyst ×7, Rose Quartz ×7, ..." (stones only)
 *   Designed for   the filter that was on while it was designed, if any
 *   _Bead order    the maker's short instruction: what goes on and where bead 1 is (hidden)
 *   _Design view   link to the numbered, printable workshop card (hidden); its random &ref=
 *                  lets the card fetch the order number from the reorder app
 *   _pattern       bead handles in ring order (hidden)
 *   _stones        {"<reorder-app stone name>": beads} for stock (hidden; no letter beads)
 *   _builder       schema version (hidden)
 * Underscore properties are hidden from the customer but show on the admin order page.
 *
 * Names: the Name tab (config.letters) turns what the shopper types into alphabet beads,
 * virtual beads with handles "abc-<design>-<letter>". The letters always take the first
 * slots (so a design link and the maker's count start at the first letter) and the ring
 * is drawn turned so they sit together at the top. They are locked on the ring: only the
 * name changes them, and Fill the rest (the "repeat" tool) / Shuffle / Surprise work on the
 * stones after them.
 *
 * Several bracelets: "Design another" (under the size, once a bracelet is complete)
 * keeps it on the page and starts the next; kept ones show as small bracelets in a strip
 * under the ring (tap one to edit it). "Add to cart" adds every finished
 * bracelet at once. They are kept in localStorage, so a reload or a later visit finds
 * them. After an add the button reads "Design another" (a copy of the same design =
 * the cart's quantity).
 *
 * Buttons: the sticky bar (phones) and, at the bottom as on the product pages, a
 * summary of every bracelet (size + design) with Add to cart and Buy it now. Buy it now
 * adds the finished bracelets and goes straight to checkout (Shopify's own button
 * checks out a single product from a form, so it can't take several bracelets).
 */
(function () {
  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const rand = (n) => Math.floor(Math.random() * n);

  // A bead render (stone_details.bead_image: round, lit, transparent) is shown as is;
  // a stone photo (the fallback) is cropped and given a painted highlight to read as a bead.
  const isRender = (b) => Boolean(b && b.imageKind === 'bead');
  const imgClass = (b) => (isRender(b) ? 'is-render' : 'is-photo');

  // A bracelet on the page: { variantId, count, slots: [bead handle | null], designedFor }.
  const isComplete = (d) => d.slots.length > 0 && !d.slots.includes(null);

  // Bead diameter and ring radius as fractions of the ring box, so every ring (the big
  // one, the small ones beside it) is fully responsive with no resize handling. `turn`
  // (in slots) puts slot `turn` at the top instead of slot 0: a name of L letters is
  // centred at the top with turn = (L - 1) / 2.
  const ringLayout = (n, turn = 0) => {
    const k = (0.94 * 2 * Math.PI) / n;
    const d = k / 2 / (1 + k / 2);
    const r = 0.5 - d / 2;
    return {
      d,
      at(i) {
        const a = ((-90 + (360 / n) * (i - turn)) * Math.PI) / 180;
        return { x: 0.5 + r * Math.cos(a) - d / 2, y: 0.5 + r * Math.sin(a) - d / 2 };
      },
    };
  };

  // Search text: lower case, no accents, words only.
  const norm = (s) =>
    String(s == null ? '' : s).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();

  // Edit distance where swapping two neighbouring letters counts once ("flourite").
  const editDistance = (a, b) => {
    if (Math.abs(a.length - b.length) > 2) return 3;
    const d = [];
    for (let i = 0; i <= a.length; i++) d.push([i]);
    for (let j = 1; j <= b.length; j++) d[0][j] = j;
    for (let i = 1; i <= a.length; i++) {
      for (let j = 1; j <= b.length; j++) {
        d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
    return d[a.length][b.length];
  };

  const KEEP_DAYS = 14; // how long the page remembers a shopper's bracelets

  class BraceletBuilder extends HTMLElement {
    connectedCallback() {
      const cat = this.querySelector('[data-bb-catalog]');
      const cfg = this.querySelector('[data-bb-config]');
      if (!cat || !cfg || !this.querySelector('[data-bb-slots]')) return;

      try {
        this.catalog = JSON.parse(cat.textContent);
        this.config = JSON.parse(cfg.textContent);
      } catch (e) {
        console.error('[bracelet-builder] bad catalogue JSON', e);
        return;
      }

      this.byHandle = {};
      this.catalog.forEach((b) => (this.byHandle[b.handle] = b));

      // Alphabet beads (the Name tab): A–Z per design, in byHandle only, so they never
      // show among the stones, in the search or in Surprise.
      const letters = this.config.letters;
      this.letters = letters && Array.isArray(letters.designs) && letters.designs.length ? letters : null;
      if (this.letters) {
        this.letters.designs.forEach((ds) => {
          for (let c = 65; c <= 90; c++) {
            const ch = String.fromCharCode(c);
            const h = 'abc-' + ds.key + '-' + ch.toLowerCase();
            this.byHandle[h] = { handle: h, kind: 'letter', letter: ch, design: ds, name: (ds.label ? ds.label + ' letter ' : 'Letter ') + ch, available: true };
          }
        });
        this.letterDesign = this.letters.designs[0].key;
      }

      this.variantId = String(this.config.variantId);
      this.count = Number(this.config.count) || 18;
      this.slots = new Array(this.count).fill(null);
      this.selected = null;
      this.history = [];
      this.tab = 'zodiac';
      this.filter = null; // { type, value }
      this.query = ''; // the stone search
      this.addedKey = null; // the design last added to the cart (see isAdded)

      this.$ = (sel) => this.querySelector(sel);
      this.$slots = this.$('[data-bb-slots]');
      this.$count = this.$('[data-bb-count]');
      this.$centerName = this.$('[data-bb-center-name]');
      this.$centerLabel = this.$('[data-bb-center-label]');
      this.$mine = this.$('[data-bb-mine]');
      this.$news = [...this.querySelectorAll('[data-bb-new]')]; // beside the size + under the summary
      this.$fill = this.$('[data-bb-tool="repeat"]'); // "Fill the rest"
      this.$palette = this.$('[data-bb-palette]');
      this.$search = this.$('[data-bb-search]');
      this.$searchClear = this.$('[data-bb-search-clear]');
      this.$chips = this.$('[data-bb-chips]');
      this.$namePanel = this.$('[data-bb-name-panel]');
      this.$name = this.$('[data-bb-name]');
      this.$nameClear = this.$('[data-bb-name-clear]');
      this.$summary = this.$('[data-bb-summary]');
      this.$summaryTitle = this.$('[data-bb-summary-title]');
      this.$summaryBeads = this.$('[data-bb-summary-beads]');
      this.$summaryBenefits = this.$('[data-bb-summary-benefits]');
      this.$price = this.$('[data-bb-price]');
      this.$adds = [...this.querySelectorAll('[data-bb-add]')]; // sticky bar + bottom button
      this.$buy = this.$('[data-bb-buy]');
      this.$buyNow = this.$('[data-bb-buynow]');
      this.$orderSummary = this.$('[data-bb-order-summary]');
      this.$orderList = this.$('[data-bb-order-list]');
      this.$orderTotal = this.$('[data-bb-order-total]');
      this.$hint = this.$('[data-bb-hint]');
      this.$toast = this.$('[data-bb-toast]');
      this.$dialog = this.$('[data-bb-dialog]');
      this.$dialogBody = this.$('[data-bb-dialog-body]');
      this.$workshop = this.$('[data-bb-workshop]');
      this.$workshopList = this.$('[data-bb-workshop-list]');
      this.$workshopText = this.$('[data-bb-workshop-text]');
      this.$workshopSize = this.$('[data-bb-workshop-size]');
      this.$orderRow = this.$('[data-bb-workshop-order]');
      this.$order = this.$('[data-bb-order]');
      this.$workshopName = this.$('[data-bb-workshop-name]');
      this.$reports = this.$('[data-bb-reports]');
      this.$reportsGrid = this.$('[data-bb-reports-grid]');
      this.$dots = this.$('[data-bb-dots]');

      // A design link (?design=handle,handle,...) loads that exact bracelet.
      // &workshop=1 makes it a read-only, numbered assembly view for the makers,
      // and &order=... puts the order number on it.
      const params = new URLSearchParams(window.location.search);
      const linked = params.get('design');
      if (linked) this.loadDesign(linked);
      this.workshop = params.get('workshop') === '1';
      if (this.workshop) {
        this.classList.add('bb--workshop');
        this.initOrder(params.get('order'));
      }

      // The bracelets on the page; the ring edits designs[active] (see commit / load).
      this.designs = [{ variantId: this.variantId, count: this.count, slots: this.slots.slice(), designedFor: '' }];
      this.active = 0;
      const restored = !this.workshop && !linked && this.restore();

      this.bind();
      this.renderAll();
      if (restored) this.toast(this.designs.length > 1 ? 'Your ' + this.designs.length + ' bracelets are still here.' : 'Your bracelet is still here.');
    }

    /* ───────────── design links + workshop view ───────────── */

    loadDesign(str) {
      const handles = String(str).split(',').map((s) => s.trim()).filter(Boolean).slice(0, 60);
      if (!handles.length) return;
      const sizes = [...this.querySelectorAll('[data-bb-size]')];
      const match =
        sizes.find((b) => Number(b.dataset.count) === handles.length) ||
        sizes.slice().sort((a, b) => Math.abs(a.dataset.count - handles.length) - Math.abs(b.dataset.count - handles.length))[0];
      if (match) {
        this.variantId = match.dataset.variantId;
        this.count = Number(match.dataset.count) || this.count;
      }
      this.slots = Array.from({ length: this.count }, (_, i) => (this.byHandle[handles[i]] ? handles[i] : null));
    }

    designUrl(slots = this.slots) {
      const base = (this.config.shopUrl || window.location.origin) + '/products/' + (this.config.productHandle || '');
      return base + '?design=' + slots.join(',') + '&workshop=1';
    }

    /**
     * The order number on the workshop card. It comes from &order= when the link
     * has it; otherwise the maker types it. Either way it prints, is kept in the
     * address bar (so a reload keeps it) and names the tab / print header.
     */
    initOrder(value) {
      if (!this.$orderRow || !this.$order) return;
      const v = String(value || '').trim().replace(/^#+/, '').slice(0, 20);
      this.$order.value = v ? '#' + v : '';
      this.$orderRow.hidden = false;
      this.syncOrder(false);
      this.$order.addEventListener('input', () => this.syncOrder(true));
      // A link from an order carries the ref it got in the cart; the reorder app knows
      // which order that ref landed in. Not found or offline: the maker types it.
      const ref = new URLSearchParams(window.location.search).get('ref');
      if (!v && ref && this.config.orderLookup) this.lookupOrder(ref);
    }

    async lookupOrder(ref) {
      try {
        const res = await fetch(this.config.orderLookup + '?ref=' + encodeURIComponent(ref), { cache: 'no-store' });
        const data = res.ok ? await res.json() : null;
        const num = String((data && data.order) || '').trim().replace(/^#+/, '').slice(0, 20);
        if (!num || this.$order.value.trim()) return; // not found, or already typed
        this.$order.value = '#' + num;
        this.syncOrder(true);
      } catch (e) {
        // offline or blocked: the maker types it
      }
    }

    // A random reference for a design link (12 letters/digits), see lookupOrder.
    newRef() {
      const abc = 'abcdefghijklmnopqrstuvwxyz0123456789';
      const bytes = new Uint8Array(12);
      if (window.crypto && crypto.getRandomValues) crypto.getRandomValues(bytes);
      else bytes.forEach((_, i) => (bytes[i] = rand(256)));
      return Array.from(bytes, (b) => abc[b % 36]).join('');
    }

    syncOrder(updateUrl) {
      const v = this.$order.value.trim();
      document.title = [v ? 'Order ' + v : '', this.nameOf(), 'Bracelet assembly card'].filter(Boolean).join(' · ');
      if (!updateUrl) return;
      const url = new URL(window.location.href);
      if (v) url.searchParams.set('order', v);
      else url.searchParams.delete('order');
      history.replaceState(history.state, '', url);
    }

    /**
     * The maker's instruction (the `_Bead order` line on the order), kept short: the
     * numbered picture on the card shows the order. What goes on (the name's letters,
     * then each stone and how many) and where to start.
     */
    beadOrderText(slots = this.slots) {
      const nm = this.nameOf(slots);
      const ds = nm ? this.byHandle[slots[0]].design || {} : {};
      const what = [nm ? nm + ' in ' + (ds.label ? ds.label.toLowerCase() + ' ' : '') + 'letters' : '', this.beadsText(slots)].filter(Boolean).join(', ');
      return (what ? what + '. ' : '') + this.startText(slots) + ', as numbered on the card.';
    }

    // Where the count starts (bead 1 on the ring): the first letter of a name, else the top.
    startText(slots = this.slots) {
      return this.nameOf(slots) ? 'Start at bead 1, the first letter, and go clockwise' : 'Start at bead 1 at the top and go clockwise';
    }

    renderWorkshop() {
      if (!this.workshop || !this.$workshop) return;
      this.$workshop.hidden = false;
      // The name first, big, so the maker can't miss it (the letters are also on the ring).
      if (this.$workshopName) {
        const nm = this.nameOf();
        const ds = nm ? this.byHandle[this.slots[0]].design || {} : {};
        this.$workshopName.hidden = !nm;
        this.$workshopName.innerHTML = nm
          ? '<span class="bb__workshop-name-label">' + esc(['Name', ds.label ? ds.label + ' letters' : ''].filter(Boolean).join(' · ')) + '</span><strong>' + esc(nm) + '</strong>'
          : '';
      }
      const size = this.sizeButton(this.variantId);
      if (this.$workshopSize) {
        this.$workshopSize.textContent = (size ? size.querySelector('.bb__size-name').textContent.trim() + ' · ' : '') + this.count + ' beads';
      }
      if (this.$workshopText) this.$workshopText.textContent = this.startText() + '.';
      // A legend, not a bead-by-bead list (the numbered ring shows the order): the name
      // once, then each stone with how many.
      const legend = [];
      const nm = this.nameOf();
      if (nm) {
        const first = this.byHandle[this.slots[0]];
        legend.push({ b: first, label: nm + ' · ' + ((first.design && first.design.label) ? first.design.label + ' letters' : 'letters'), n: nm.length, wide: true });
      }
      this.tally().forEach((t) => {
        if (t.bead.kind !== 'letter') legend.push({ b: t.bead, label: t.bead.name, n: t.count });
      });
      const missing = this.slots.filter((h) => !h).length;
      if (missing) legend.push({ b: null, label: 'Missing bead, check the order', n: missing });
      this.$workshopList.innerHTML = legend
        .map((x) =>
          '<li' + (x.wide ? ' class="is-wide"' : '') + '><span class="bb__wl-bead' + (isRender(x.b) ? ' has-render' : '') + '">' + this.beadFace(x.b) + '</span>' +
          '<span class="bb__wl-name">' + esc(x.label) + '</span><span class="bb__wl-count">×' + x.n + '</span></li>'
        )
        .join('');
    }

    /* ───────────── events ───────────── */

    bind() {
      // Clicking the dimmed backdrop (the <dialog> itself, not its content) closes it.
      if (this.$dialog) {
        this.$dialog.addEventListener('click', (e) => {
          if (e.target === this.$dialog) this.$dialog.close();
        });
      }

      // Stone pages (phones): keep the dots in step with the swipe.
      if (this.$palette) this.$palette.addEventListener('scroll', () => this.syncDots(), { passive: true });

      if (this.$search) {
        this.$search.addEventListener('input', () => this.setQuery(this.$search.value));
        this.$search.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.$search.blur(); // closes the phone keyboard so the results show
          } else if (e.key === 'Escape') {
            this.setQuery('');
          }
        });
        // Phones: the add-to-cart bar steps aside while typing (it would ride the keyboard).
        this.$search.addEventListener('focus', () => this.classList.add('is-searching'));
        this.$search.addEventListener('blur', () => this.classList.remove('is-searching'));

        // "Search stones", "Stones" or none (the icon says it) — whatever fits the field.
        // Checked again whenever the field changes width (screen, the tabs' web font arriving)
        // or a font finishes loading (the measure depends on it).
        this.searchLabel = this.$search.getAttribute('placeholder') || 'Search stones';
        const fit = () => this.fitSearchPlaceholder();
        fit();
        if ('ResizeObserver' in window) new ResizeObserver(fit).observe(this.$search);
        else window.addEventListener('resize', fit);
        if (document.fonts) {
          if (document.fonts.ready) document.fonts.ready.then(fit);
          if (document.fonts.addEventListener) document.fonts.addEventListener('loadingdone', fit);
        }
      }

      // The name goes onto the bracelet as it is typed.
      if (this.$name) {
        this.$name.addEventListener('input', () => this.setName(this.$name.value));
        this.$name.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.$name.blur();
          }
        });
        this.$name.addEventListener('focus', () => this.classList.add('is-searching'));
        this.$name.addEventListener('blur', () => this.classList.remove('is-searching'));
      }

      // Phones: the sticky bar also steps aside while the bottom buttons are on screen.
      if (this.$buy && 'IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          entries.forEach((en) => this.classList.toggle('bb--buy-in-view', en.isIntersecting));
        }).observe(this.$buy);
      }

      this.addEventListener('click', (e) => {
        // Certificate viewer: tap the report to zoom in / out.
        const zoom = e.target.closest('[data-bb-cert-zoom]');
        if (zoom) return zoom.classList.toggle('is-zoomed');
        const t = e.target.closest('button');
        if (!t || !this.contains(t)) return;

        if (t.hasAttribute('data-bb-print')) return window.print();
        if (t.matches('.bb__bead')) return this.workshop ? null : this.onSlot(Number(t.dataset.index));
        if (t.dataset.bbAddBead) {
          if (this.$dialog && this.$dialog.open && this.$dialog.contains(t)) this.$dialog.close();
          return this.place(t.dataset.bbAddBead);
        }
        if (t.dataset.bbCert) return this.showCert(t.dataset.bbCert);
        if (t.dataset.bbPage != null) {
          this.$palette.scrollTo({ left: Number(t.dataset.bbPage) * this.pageStride(), behavior: 'smooth' });
          return;
        }
        if (t.dataset.bbInfo) return this.showInfo(t.dataset.bbInfo);
        if (t.dataset.bbSwitch != null) return this.switchTo(Number(t.dataset.bbSwitch));
        if (t.dataset.bbEdit != null) return this.editBracelet(Number(t.dataset.bbEdit));
        if (t.dataset.bbRemove != null) return this.removeBracelet(Number(t.dataset.bbRemove));
        if (t.hasAttribute('data-bb-new')) return this.designAnother();
        if (t.hasAttribute('data-bb-buynow')) return this.buyNow();
        if (t.hasAttribute('data-bb-search-clear')) {
          this.setQuery('');
          if (this.$search) this.$search.focus();
          return;
        }
        if (t.hasAttribute('data-bb-name-clear')) {
          this.setName('');
          if (this.$name) this.$name.focus();
          return;
        }
        if (t.hasAttribute('data-bb-size')) return this.setSize(t);
        if (t.dataset.bbTab) return this.setTab(t.dataset.bbTab);
        if (t.hasAttribute('data-bb-chip')) return this.setFilter(t.dataset.value || null);
        if (t.dataset.bbTool) return this.tool(t.dataset.bbTool);
        if (t.hasAttribute('data-bb-add')) return this.isAdded() ? this.newDesign() : this.addToCart();
      });
    }

    /* ───────────── state helpers ───────────── */

    // Undo steps hold every bracelet on the page, so adding, deleting and editing one
    // all undo the same way.
    snapshot() {
      this.commit();
      this.history.push(JSON.parse(JSON.stringify({ designs: this.designs, active: this.active })));
      if (this.history.length > 50) this.history.shift();
    }

    // Write the ring back into the list of bracelets. "Designed for" follows the filter
    // that was on when its beads last changed.
    commit() {
      const prev = this.designs[this.active];
      const slots = this.slots.slice();
      let designedFor = prev ? prev.designedFor || '' : '';
      if (this.filter && (!prev || prev.slots.join(',') !== slots.join(','))) designedFor = this.filterLabel();
      this.designs[this.active] = { variantId: this.variantId, count: this.count, slots, designedFor };
    }

    // Put bracelet i on the ring.
    load(i) {
      const d = this.designs[i];
      this.active = i;
      this.variantId = String(d.variantId);
      this.count = d.count;
      this.slots = d.slots.slice();
      this.selected = null;
      this.syncSizes();
    }

    sizeButton(variantId) {
      return [...this.querySelectorAll('[data-bb-size]')].find((b) => b.dataset.variantId === String(variantId)) || null;
    }

    /* ───────────── names (alphabet beads) ───────────── */

    isLetter(h) {
      const b = h ? this.byHandle[h] : null;
      return Boolean(b && b.kind === 'letter');
    }

    // The name on a bracelet: its letter beads, which always take the first slots.
    nameOf(slots = this.slots) {
      let s = '';
      for (const h of slots) {
        if (!this.isLetter(h)) break;
        s += this.byHandle[h].letter;
      }
      return s;
    }

    // The turn that centres a bracelet's name at the top of its ring (see ringLayout).
    turnFor(slots) {
      const n = this.nameOf(slots).length;
      return n ? (n - 1) / 2 : 0;
    }

    nameMax() {
      return Math.max(0, Math.min(Number(this.letters && this.letters.max) || 12, this.count - 1));
    }

    /**
     * Put a name on the bracelet as it is typed: its letters take the first slots, the
     * stones that followed the old name follow the new one, and the last of them drop off
     * when the name gets longer (empty slots come back when it gets shorter). A burst of
     * typing is one undo step.
     */
    setName(raw) {
      if (!this.letters) return;
      const clean = String(raw || '').toUpperCase().normalize('NFD').replace(/[^A-Z]/g, '').slice(0, this.nameMax());
      if (this.$name && this.$name.value.toUpperCase() !== clean) this.$name.value = clean;
      if (this.$nameClear) this.$nameClear.hidden = !clean;
      const old = this.nameOf();
      if (clean === old) return;
      const now = Date.now();
      if (!this._namedAt || now - this._namedAt > 1500) this.snapshot();
      this._namedAt = now;
      const design = old ? this.byHandle[this.slots[0]].design.key : this.letterDesign;
      const slots = [...clean].map((ch) => 'abc-' + design + '-' + ch.toLowerCase()).concat(this.slots.slice(old.length)).slice(0, this.count);
      while (slots.length < this.count) slots.push(null);
      this.slots = slots;
      this.selected = null;
      if (!clean) this.setHint('Name removed.');
      else this.setHint(this.slots.includes(null) ? 'Now tap stones for the rest of the bracelet.' : 'Tap a stone on the bracelet to change it.');
      this.renderDesign();
    }

    // The name field follows the bracelet on the ring (undo, another bracelet, a reload).
    // While typing the two already agree (setName ran first), so the caret stays put.
    renderName() {
      if (!this.$name) return;
      const nm = this.nameOf();
      if (this.$name.value.toUpperCase() !== nm) this.$name.value = nm;
      if (this.$nameClear) this.$nameClear.hidden = !this.$name.value;
    }

    // A bead inside a ring or list: a letter is drawn (every letter exact, scales with the
    // bead), a stone is its image.
    beadFace(b) {
      if (!b) return '';
      if (b.kind === 'letter') {
        const ds = b.design || {};
        const f = ds.face || [];
        return '<span class="bb__abc" style="--abc-1:' + esc(f[0] || '#fbeedd') + ';--abc-2:' + esc(f[1] || '#e2c19c') + ';--abc-3:' + esc(f[2] || '#b68b62') +
          ';--abc-ink:' + esc(ds.ink || '#1c1917') + '"><svg viewBox="0 0 100 100" aria-hidden="true" focusable="false"><text x="50" y="50">' + esc(b.letter) + '</text></svg></span>';
      }
      return b.image ? '<img class="' + imgClass(b) + '" src="' + esc(b.image) + '" alt="" draggable="false">' : '';
    }

    matches(bead) {
      if (!this.filter) return true;
      const { type, value } = this.filter;
      const list = type === 'zodiac' ? bead.zodiac : type === 'intent' ? bead.intent : bead.chakras;
      return Array.isArray(list) && (list.includes(value) || list.includes('All'));
    }

    // A search looks through every stone; otherwise the sign / intent / chakra filter applies.
    visibleBeads() {
      return this.query ? this.search(this.query) : this.catalog.filter((b) => this.matches(b));
    }

    /**
     * Stones for the search box, best first: a name word starting with the search, then a
     * name containing it, then the benefit, sign, intent or chakra. Every word typed must
     * match. When nothing matches, names within a typo or two ("amethist", "lapiz") are
     * offered instead.
     */
    search(q) {
      const words = norm(q).split(' ').filter(Boolean);
      if (!words.length) return this.catalog.slice();
      const found = [];
      const near = [];
      this.catalog.forEach((b, i) => {
        const names = norm(b.name + ' ' + (b.stone || '')).split(' ');
        const nameText = names.join(' ');
        const other = norm([b.benefit].concat(b.zodiac || [], b.intent || [], b.chakras || []).join(' '));
        let score = 0;
        let typos = 0;
        let exact = true;
        for (const w of words) {
          if (names.some((n) => n.startsWith(w))) score += 3;
          else if (nameText.includes(w)) score += 2;
          else if (other.includes(w)) score += 1;
          else {
            exact = false;
            // Short words get no typo allowance ("moon" must not find "Mookaite").
            const allowed = w.length >= 7 ? 2 : w.length >= 5 ? 1 : 0;
            const best = Math.min(...names.map((n) => Math.min(editDistance(w, n), n.length > w.length ? editDistance(w, n.slice(0, w.length)) : 3)));
            if (best > allowed) return;
            typos += best;
          }
        }
        if (exact) found.push({ b, score, i });
        else near.push({ b, typos, i });
      });
      if (found.length) return found.sort((x, y) => y.score - x.score || x.i - y.i).map((x) => x.b);
      return near.sort((x, y) => x.typos - y.typos || x.i - y.i).map((x) => x.b);
    }

    filterLabel() {
      if (!this.filter) return '';
      return this.filter.type === 'chakra' ? this.filter.value + ' chakra' : this.filter.value;
    }

    /* ───────────── actions ───────────── */

    place(handle) {
      const bead = this.byHandle[handle];
      if (!bead || !bead.available) return;
      let idx = this.selected !== null ? this.selected : this.slots.indexOf(null);
      if (idx === -1) {
        this.toast('Your bracelet is full. Tap a bead on it to swap it.');
        return;
      }
      this.snapshot();
      this.slots[idx] = handle;
      this.selected = null;
      this.setHint(this.slots.includes(null) ? 'Keep going, or tap Fill the rest.' : 'Your bracelet is complete.');
      this.renderDesign();
    }

    onSlot(i) {
      // Letters are locked on the ring: they change with the name.
      if (this.isLetter(this.slots[i])) {
        this.selected = null;
        this.setTab('name');
        this.setHint('To change the letters, edit the name.');
        return this.renderDesign();
      }
      if (this.selected === i) {
        if (this.slots[i]) {
          this.snapshot();
          this.slots[i] = null;
          this.setHint('Bead removed.');
        }
        this.selected = null;
      } else {
        this.selected = i;
        this.setHint(this.slots[i] ? 'Tap a stone to swap this bead, or tap it to remove.' : 'Tap a stone to place it here.');
      }
      this.renderDesign();
    }

    setSize(btn) {
      const n = Number(btn.dataset.count) || this.count;
      if (btn.dataset.variantId === this.variantId && n === this.count) return;
      this.snapshot();
      if (n > this.count) {
        this.slots = this.slots.concat(new Array(n - this.count).fill(null));
      } else if (n < this.count) {
        const dropped = this.slots.slice(n).filter(Boolean).length;
        this.slots = this.slots.slice(0, n);
        if (dropped) this.toast(dropped + (dropped === 1 ? ' bead was' : ' beads were') + ' removed for the smaller size. Undo to restore.');
      }
      this.count = n;
      this.variantId = btn.dataset.variantId;
      this.selected = null;
      this.syncSizes();
      this.renderDesign();
    }

    syncSizes() {
      this.querySelectorAll('[data-bb-size]').forEach((b) => {
        b.setAttribute('aria-checked', b.dataset.variantId === this.variantId ? 'true' : 'false');
      });
    }

    setTab(tab) {
      this.tab = tab;
      this.querySelectorAll('[data-bb-tab]').forEach((t) => t.setAttribute('aria-selected', t.dataset.bbTab === tab ? 'true' : 'false'));
      // The Name tab swaps the chips for the name field (a sign / intent filter stays on).
      const naming = tab === 'name';
      if (this.$namePanel) this.$namePanel.hidden = !naming;
      if (this.$chips) this.$chips.hidden = naming;
      if (naming) return this.renderName();
      this.renderChips();
    }

    setFilter(value) {
      this.filter = value ? { type: this.tab, value } : null;
      // Browsing by sign / intent / chakra takes over from a search.
      this.query = '';
      if (this.$search) this.$search.value = '';
      if (this.$searchClear) this.$searchClear.hidden = true;
      this.renderChips();
      this.renderPalette();
    }

    // Measured at the placeholder's size (--bb-ph-size) against the empty field's text room
    // (its right padding is 12px while empty, see :placeholder-shown in the CSS).
    fitSearchPlaceholder() {
      const input = this.$search;
      if (!input || !input.clientWidth) return;
      const cs = getComputedStyle(input);
      const size = cs.getPropertyValue('--bb-ph-size').trim() || '14px';
      const ctx = (this._measure = this._measure || document.createElement('canvas').getContext('2d'));
      ctx.font = cs.fontStyle + ' ' + cs.fontWeight + ' ' + size + ' ' + cs.fontFamily;
      const room = input.clientWidth - parseFloat(cs.paddingLeft) - 12;
      input.placeholder = [this.searchLabel, 'Stones'].find((t) => ctx.measureText(t).width + 2 <= room) || '';
    }

    setQuery(value) {
      const raw = String(value || '').slice(0, 40);
      if (this.$search && this.$search.value !== raw) this.$search.value = raw;
      if (this.$searchClear) this.$searchClear.hidden = !raw;
      const q = raw.trim();
      if (q === this.query) return;
      this.query = q;
      this.renderPalette();
    }

    // "Design another" (beside the size, and under the summary). Once this bracelet is in
    // the cart it starts a fresh ring; otherwise it keeps this one on the page and starts the
    // next, when it is complete. Either way the bracelet comes into view to design on.
    designAnother() {
      if (this.isAdded()) return this.newDesign();
      const left = this.slots.filter((s) => !s).length;
      if (left) {
        this.toast('Finish this bracelet first: ' + left + ' more bead' + (left === 1 ? '' : 's') + '. Then design another.');
      } else {
        this.newBracelet();
      }
      this.showRing();
    }

    // Keep this bracelet on the page, start the next one in the same size.
    newBracelet() {
      if (this.slots.includes(null) || this.isAdded()) return;
      this.snapshot();
      this.designs.push({ variantId: this.variantId, count: this.count, slots: new Array(this.count).fill(null), designedFor: '' });
      this.load(this.designs.length - 1);
      const n = this.designs.length;
      this.setHint('Bracelet ' + n + ': tap stones to design it.');
      this.renderDesign();
      this.toast('Bracelet ' + (n - 1) + ' saved. Now design bracelet ' + n + '.');
    }

    // Edit another of the shopper's bracelets. A bracelet left without a single bead is
    // dropped on the way.
    switchTo(i) {
      if (i === this.active || !this.designs[i]) return;
      if (this.designs.length > 1 && !this.slots.some(Boolean)) {
        this.designs.splice(this.active, 1);
        if (i > this.active) i -= 1;
      }
      this.load(i);
      this.setHint('Editing bracelet ' + (i + 1) + '.');
      this.renderDesign();
    }

    // From the summary: edit that bracelet on the ring and bring the ring into view.
    editBracelet(i) {
      this.switchTo(i);
      this.showRing();
    }

    showRing() {
      const ring = this.$('[data-bb-ring]');
      if (!ring) return;
      const r = ring.getBoundingClientRect();
      if (r.top < 0 || r.bottom > window.innerHeight) ring.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    // Remove bracelet i (the × in the summary). The only bracelet on the page is emptied
    // instead. Undo brings it back.
    removeBracelet(i) {
      if (!this.designs[i]) return;
      this.snapshot();
      if (this.designs.length < 2) {
        this.slots = new Array(this.count).fill(null);
        this.selected = null;
        this.setHint('Tap a stone to start again.');
        this.renderDesign();
        return this.toast('Bracelet removed. Undo brings it back.');
      }
      this.designs.splice(i, 1);
      const next = i < this.active ? this.active - 1 : i === this.active ? Math.max(0, i - 1) : this.active;
      this.load(next);
      this.setHint('Editing bracelet ' + (this.active + 1) + '.');
      this.renderDesign();
      this.toast('Bracelet ' + (i + 1) + ' removed. Undo brings it back.');
    }

    tool(name) {
      const filled = this.slots.filter(Boolean);
      // Repeat, Shuffle and Surprise work on the stones after the name (L = its letters).
      const L = this.nameOf().length;
      const stones = this.slots.slice(L).filter(Boolean);

      if (name === 'undo') {
        const prev = this.history.pop();
        if (!prev) return this.toast('Nothing to undo.');
        this.designs = prev.designs;
        this.load(prev.active);
        return this.renderDesign();
      }

      if (name === 'clear') {
        if (!filled.length) return;
        this.snapshot();
        this.slots = new Array(this.count).fill(null);
        this.selected = null;
        this.setHint('Cleared. Tap a stone to start again.');
        return this.renderDesign();
      }

      if (name === 'repeat') {
        if (!stones.length) return this.toast('Add a few stones first, then tap Fill the rest.');
        this.snapshot();
        this.slots = this.slots.map((h, i) => (i < L ? h : stones[(i - L) % stones.length]));
        this.selected = null;
        this.setHint('Your pattern fills the bracelet. Tap a bead to change it.');
        return this.renderDesign();
      }

      if (name === 'shuffle') {
        if (stones.length < 2) return this.toast('Add at least two stones to shuffle.');
        this.snapshot();
        const pool = stones.slice();
        for (let i = pool.length - 1; i > 0; i--) {
          const j = rand(i + 1);
          [pool[i], pool[j]] = [pool[j], pool[i]];
        }
        let k = 0;
        this.slots = this.slots.map((s, i) => (i < L || !s ? s : pool[k++]));
        return this.renderDesign();
      }

      if (name === 'surprise') {
        const pool = this.visibleBeads().filter((b) => b.available);
        if (!pool.length) return this.toast(this.query ? 'No stones match your search.' : 'No stones match this filter.');
        const picks = [];
        const want = Math.min(pool.length, 2 + rand(2)); // 2 or 3 stones
        while (picks.length < want) {
          const b = pool[rand(pool.length)].handle;
          if (!picks.includes(b)) picks.push(b);
        }
        const motifs = [[0, 1], [0, 1, 2], [0, 0, 1], [0, 1, 1, 2]].filter((m) => Math.max(...m) < picks.length);
        const motif = motifs[rand(motifs.length)];
        this.snapshot();
        this.slots = this.slots.map((h, i) => (i < L ? h : picks[motif[(i - L) % motif.length]]));
        this.selected = null;
        this.setHint(this.filter ? 'A mix for ' + this.filterLabel() + '.' : 'Tap any bead to change it.');
        return this.renderDesign();
      }
    }

    /* ───────────── rendering ───────────── */

    renderAll() {
      this.syncSizes();
      this.renderChips();
      this.renderPalette();
      this.renderDesign();
    }

    renderDesign() {
      this.commit();
      this.renderRing();
      this.renderMine();
      this.renderSummary();
      this.renderCta();
      this.renderReports();
      this.renderOrder();
      this.renderName();
      this.persist();
    }

    renderRing() {
      const n = this.count;
      const ring = ringLayout(n, this.turnFor(this.slots));
      const frag = document.createDocumentFragment();

      for (let i = 0; i < n; i++) {
        const { x, y } = ring.at(i);
        const h = this.slots[i];
        const bead = h ? this.byHandle[h] : null;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bb__bead' + (bead ? '' : ' is-empty') + (isRender(bead) ? ' has-render' : '') + (bead && bead.kind === 'letter' ? ' is-letter' : '') + (this.selected === i ? ' is-selected' : '');
        btn.dataset.index = i;
        btn.style.left = x * 100 + '%';
        btn.style.top = y * 100 + '%';
        btn.style.width = btn.style.height = ring.d * 100 + '%';
        btn.setAttribute('aria-label', 'Bead ' + (i + 1) + ': ' + (bead ? bead.name : 'empty'));
        if (bead && bead.kind === 'letter') {
          btn.insertAdjacentHTML('beforeend', this.beadFace(bead));
        } else if (bead && bead.image) {
          const img = document.createElement('img');
          img.className = imgClass(bead);
          img.src = bead.image;
          img.alt = '';
          img.draggable = false;
          img.decoding = 'async';
          btn.appendChild(img);
        }
        if (this.workshop) {
          const num = document.createElement('span');
          num.className = 'bb__bead-num';
          num.textContent = i + 1;
          btn.appendChild(num);
        }
        frag.appendChild(btn);
      }
      this.$slots.replaceChildren(frag);
      const filled = this.slots.filter(Boolean).length;
      if (this.$count) this.$count.textContent = filled + ' / ' + n;
      // An empty bracelet says what to do.
      if (this.$centerLabel) this.$centerLabel.textContent = filled || this.workshop ? 'beads' : 'tap a stone';
      this.renderWorkshop();
    }

    // The shopper's bracelets in a strip under the ring (from two on): small rings, the one
    // on the big ring outlined. Tap one to edit it.
    renderMine() {
      const multi = !this.workshop && this.designs.length > 1;
      if (this.$centerName) {
        this.$centerName.hidden = !multi;
        this.$centerName.textContent = multi ? 'Bracelet ' + (this.active + 1) : '';
      }
      if (!this.$mine) return;
      this.$mine.hidden = !multi;
      if (!multi) {
        this.$mine.innerHTML = '';
        return;
      }
      this.$mine.innerHTML = this.designs
        .map((d, i) => {
          const filled = d.slots.filter(Boolean).length;
          const on = i === this.active;
          return '<button type="button" class="bb__mini' + (on ? ' is-active' : '') + (filled < d.count ? ' is-unfinished' : '') + '" data-bb-switch="' + i + '"' +
            (on ? ' aria-current="true"' : '') + ' aria-label="Bracelet ' + (i + 1) + ': ' + filled + ' of ' + d.count + ' beads">' +
            this.miniRing(d) + '<span class="bb__mini-num">' + (i + 1) + '</span></button>';
        })
        .join('');
      // Keep the one being edited in view when the strip scrolls sideways.
      const on = this.$mine.querySelector('.is-active');
      if (on && this.$mine.scrollWidth > this.$mine.clientWidth) {
        const left = on.getBoundingClientRect().left - this.$mine.getBoundingClientRect().left + this.$mine.scrollLeft;
        if (left < this.$mine.scrollLeft || left + on.offsetWidth > this.$mine.scrollLeft + this.$mine.clientWidth) this.$mine.scrollLeft = left - 8;
      }
    }

    // A small picture of a bracelet (beside the ring, in the summary).
    miniRing(d) {
      const ring = ringLayout(d.count, this.turnFor(d.slots));
      const size = (ring.d * 100).toFixed(2) + '%';
      return '<span class="bb__ringmini" aria-hidden="true">' + d.slots
        .map((h, j) => {
          const b = h ? this.byHandle[h] : null;
          const p = ring.at(j);
          return '<span class="bb__mini-bead' + (b ? '' : ' is-empty') + '" style="left:' + (p.x * 100).toFixed(2) + '%;top:' + (p.y * 100).toFixed(2) + '%;width:' + size + ';height:' + size + '">' +
            this.beadFace(b) + '</span>';
        })
        .join('') + '</span>';
    }

    // The summary above the buy box: every bracelet with its size and design, its price (or
    // how many beads it still needs) and, with several, the total. Tap one to edit it, × to
    // remove it.
    renderOrder() {
      if (!this.$orderSummary) return;
      const shown = this.designs.map((d, i) => ({ d, i })).filter((x) => x.d.slots.some(Boolean));
      // Nothing left to add once the bracelets are in the cart.
      this.$orderSummary.hidden = this.workshop || !shown.length || this.isAdded();
      if (this.$orderSummary.hidden) return;
      const multi = this.designs.length > 1;
      this.$orderList.innerHTML = shown
        .map(({ d, i }) => {
          const size = this.sizeButton(d.variantId);
          const sizeName = size ? size.querySelector('.bb__size-name').textContent.trim() : '';
          const left = d.slots.filter((s) => !s).length;
          const title = (multi ? 'Bracelet ' + (i + 1) + ' · ' : '') + (sizeName ? sizeName + ' · ' : '') + d.count + ' beads';
          return '<li><button type="button" class="bb__order-item' + (i === this.active && multi ? ' is-active' : '') + '" data-bb-edit="' + i + '" aria-label="Edit ' + esc(title) + '">' +
            this.miniRing(d) +
            '<span class="bb__order-text"><span class="bb__order-title">' + esc(title) + '</span>' +
            '<span class="bb__order-beads">' + esc(this.designText(d.slots)) + '</span></span>' +
            '<span class="bb__order-price' + (left ? ' is-unfinished' : '') + '">' +
              esc(left ? left + ' more bead' + (left === 1 ? '' : 's') : this.priceText(d.variantId)) + '</span>' +
          '</button>' +
          '<button type="button" class="bb__order-remove" data-bb-remove="' + i + '" aria-label="Remove ' + esc(title) + '">&times;</button></li>';
        })
        .join('');
      if (!this.$orderTotal) return;
      const ready = this.designs.filter(isComplete);
      const unfinished = shown.filter((x) => !isComplete(x.d));
      const total = multi && ready.length
        ? '<span>Total · ' + ready.length + ' bracelet' + (ready.length === 1 ? '' : 's') + '</span><strong>' +
          esc(this.formatMoney(ready.reduce((sum, d) => sum + this.priceCents(d.variantId), 0))) + '</strong>'
        : '';
      let note = '';
      if (unfinished.length === 1 && ready.length) {
        const left = unfinished[0].d.slots.filter((s) => !s).length;
        note = 'Bracelet ' + (unfinished[0].i + 1) + ' needs ' + left + ' more bead' + (left === 1 ? '' : 's') + ' before it can be added.';
      } else if (unfinished.length > 1 && ready.length) {
        note = 'Unfinished bracelets are added once they are complete.';
      }
      if (note) note = '<small>' + esc(note) + '</small>';
      this.$orderTotal.hidden = !total && !note;
      this.$orderTotal.innerHTML = total + note;
    }

    renderChips() {
      if (this.tab === 'name') return; // the name field shows instead
      const values = this.config[this.tab] || [];
      const available = values.filter((v) => this.catalog.some((b) => {
        const list = this.tab === 'zodiac' ? b.zodiac : this.tab === 'intent' ? b.intent : b.chakras;
        return Array.isArray(list) && (list.includes(v) || list.includes('All'));
      }));
      const active = this.filter && this.filter.type === this.tab ? this.filter.value : null;
      const chip = (v, label) =>
        '<button type="button" class="bb__chip" data-bb-chip data-value="' + esc(v || '') + '" aria-pressed="' +
        ((active || '') === (v || '') ? 'true' : 'false') + '">' + esc(label) + '</button>';
      this.$chips.innerHTML = chip('', 'All stones') + available.map((v) => chip(v, v)).join('');
    }

    // Compact cards (bead, name, ONE benefit; the rest is behind the "i"), in pages of
    // nine. On phones the pages sit side by side and swipe (3 × 3 in view under the
    // bracelet); on desktop the page wrappers are display:contents, so it is one grid.
    renderPalette() {
      const list = this.visibleBeads();
      if (!list.length) {
        this.$palette.innerHTML = this.query
          ? '<p class="bb__none">No stones match “' + esc(this.query) + '”.</p>'
          : '<p class="bb__none">No stones for ' + esc(this.filterLabel()) + ' in this collection yet.</p>';
        if (this.$dots) this.$dots.innerHTML = '';
        return;
      }
      const card = (b) =>
        '<div class="bb__card' + (b.available ? '' : ' is-oos') + '">' +
          '<button type="button" class="bb__card-add" data-bb-add-bead="' + esc(b.handle) + '" aria-label="Add ' + esc(b.name) + '"' + (b.available ? '' : ' disabled') + '>' +
            '<span class="bb__card-img' + (isRender(b) ? ' has-render' : '') + '">' + (b.image ? '<img class="' + imgClass(b) + '" src="' + esc(b.image) + '" alt="" loading="lazy" draggable="false">' : '') + '</span>' +
            '<span class="bb__card-name">' + esc(b.name) + '</span>' +
            '<span class="bb__card-benefit">' + esc(b.available ? b.benefit || '' : 'Out of stock') + '</span>' +
          '</button>' +
          '<button type="button" class="bb__card-info" data-bb-info="' + esc(b.handle) + '" aria-label="About ' + esc(b.name) + '">i</button>' +
        '</div>';
      const pages = [];
      for (let i = 0; i < list.length; i += 9) pages.push(list.slice(i, i + 9));
      this.$palette.innerHTML = pages.map((p) => '<div class="bb__page">' + p.map(card).join('') + '</div>').join('');
      this.$palette.scrollLeft = 0;
      if (this.$dots) {
        this.$dots.innerHTML = pages.length > 1
          ? pages.map((_, i) =>
              '<button type="button" class="bb__dot" data-bb-page="' + i + '" aria-label="Stones, page ' + (i + 1) + ' of ' + pages.length + '"' +
              (i === 0 ? ' aria-current="true"' : '') + '></button>'
            ).join('')
          : '';
      }
    }

    // Distance between two stone pages: the palette's width plus its gap.
    pageStride() {
      return (this.$palette.clientWidth || 1) + (parseFloat(getComputedStyle(this.$palette).columnGap) || 0);
    }

    syncDots() {
      if (!this.$dots || !this.$dots.children.length) return;
      const k = Math.round(this.$palette.scrollLeft / this.pageStride());
      [...this.$dots.children].forEach((d, i) => {
        if (i === k) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
    }

    // Lab reports for the stones in THIS design, under "Your bracelet": one per stone,
    // in the order they appear on the bracelet; tap to open it large.
    renderReports() {
      if (!this.$reports || !this.$reportsGrid) return;
      const stones = this.tally().map((t) => t.bead).filter((b) => b && b.cert);
      this.$reports.hidden = !stones.length;
      this.$reportsGrid.innerHTML = stones
        .map((b) =>
          '<button type="button" class="bb__report" data-bb-cert="' + esc(b.handle) + '" aria-label="Lab test report: ' + esc(b.name) + '">' +
            '<img src="' + esc(b.cert) + '" alt="" loading="lazy" draggable="false">' +
            '<span class="bb__report-name">' + esc(b.name) + '</span>' +
          '</button>'
        )
        .join('');
    }

    tally(slots = this.slots) {
      const order = [];
      const counts = {};
      slots.forEach((h) => {
        if (!h) return;
        if (!counts[h]) order.push(h);
        counts[h] = (counts[h] || 0) + 1;
      });
      return order.map((h) => ({ bead: this.byHandle[h], count: counts[h] }));
    }

    // The stones of a bracelet (the name's letters are its own line).
    beadsText(slots = this.slots) {
      return this.tally(slots).filter((t) => t.bead.kind !== 'letter').map((t) => t.bead.name + ' ×' + t.count).join(', ');
    }

    // A bracelet as the summaries show it: “NAME” · the stones.
    designText(slots = this.slots) {
      const nm = this.nameOf(slots);
      return [nm ? '“' + nm + '”' : '', this.beadsText(slots)].filter(Boolean).join(' · ');
    }

    // Beads per stone, keyed by the reorder app's stone name (bead.reorder_key), so
    // the reorder app can book the stock without looking beads up. Letter beads are
    // bought as needed, so no stock is kept for them.
    stonesJson(slots = this.slots) {
      const counts = {};
      this.tally(slots).forEach((t) => {
        if (t.bead.kind === 'letter') return;
        const k = t.bead.stoneKey || t.bead.name;
        counts[k] = (counts[k] || 0) + t.count;
      });
      return JSON.stringify(counts);
    }

    renderSummary() {
      if (this.$summaryTitle) {
        this.$summaryTitle.textContent = !this.workshop && this.designs.length > 1 ? 'Bracelet ' + (this.active + 1) : 'Your bracelet';
      }
      const t = this.tally();
      if (!t.length) {
        this.$summary.hidden = true;
        return;
      }
      this.$summary.hidden = false;
      this.$summaryBeads.textContent = this.designText();
      const benefits = [...new Set(t.map((x) => x.bead.benefit).filter(Boolean))];
      this.$summaryBenefits.textContent = benefits.length ? 'Brings you: ' + benefits.join(' · ') : '';
    }

    // Both add buttons (sticky bar, bottom) read the same; Buy it now needs a finished
    // bracelet (or, once added, goes straight to checkout).
    renderCta() {
      const added = this.isAdded();
      const ready = this.designs.filter(isComplete);
      this.$adds.forEach((b) => b.classList.toggle('is-added', added));
      // "Design another" stays in view; dimmed until this bracelet is complete.
      this.$news.forEach((b) => b.classList.toggle('is-waiting', !added && this.slots.includes(null)));
      // "Fill the rest" stands out once a stone is on and beads are still empty.
      if (this.$fill) {
        const L = this.nameOf().length;
        this.$fill.classList.toggle('is-ready', !added && this.slots.includes(null) && this.slots.slice(L).some(Boolean));
      }
      if (this.$buyNow) this.$buyNow.disabled = !added && !ready.length;
      this.$price.textContent = this.priceText(this.variantId);
      if (added) return this.setAddButtons('Design another', false);
      const left = this.slots.filter((s) => !s).length;
      if (this.designs.length > 1 && ready.length) {
        // Several bracelets: every finished one goes to the cart; the price is their total.
        const total = ready.reduce((sum, d) => sum + this.priceCents(d.variantId), 0);
        this.$price.textContent = this.formatMoney(total) || this.$price.textContent;
        return this.setAddButtons('Add ' + ready.length + ' to cart', false);
      }
      if (left > 0) this.setAddButtons('Add ' + left + ' more bead' + (left === 1 ? '' : 's'), true);
      else this.setAddButtons('Add to cart', false);
    }

    setAddButtons(text, disabled) {
      this.$adds.forEach((b) => {
        b.textContent = text;
        b.disabled = disabled;
      });
    }

    priceText(variantId) {
      const b = this.sizeButton(variantId);
      return (b && b.dataset.price) || this.$price.textContent;
    }

    priceCents(variantId) {
      const b = this.sizeButton(variantId);
      return Number(b && b.dataset.priceCents) || 0;
    }

    /**
     * Money in the shopper's currency, read off a Liquid-formatted sample of 1,234,567.89
     * ("AED 1,234,567.89", "1.234.567,89 €", "¥1,234,568") so totals look like every other
     * price. Whole amounts drop their ".00", like Liquid's money_without_trailing_zeros used
     * for the prices on this page. '' when the sample can't be read.
     */
    formatMoney(cents) {
      const sample = String(this.config.moneySample || '');
      const m = sample.match(/\d(?:[\d.,'   ]*\d)?/);
      if (!m) return '';
      const num = m[0];
      const groups = num.split(/\D/);
      const seps = num.match(/\D/g) || [];
      const lastSep = seps[seps.length - 1];
      let decimals = 0;
      let decSep = '';
      let thouSep = seps[0] || '';
      if (seps.length && (groups[groups.length - 1].length !== 3 || (seps.length > 1 && lastSep !== seps[0]))) {
        decimals = groups[groups.length - 1].length;
        decSep = lastSep;
        thouSep = seps.length > 1 ? seps[0] : '';
      }
      const [whole, frac] = (cents / 100).toFixed(decimals).split('.');
      const out = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thouSep) + (decimals && /[1-9]/.test(frac) ? decSep + frac : '');
      return sample.slice(0, m.index) + out + sample.slice(m.index + num.length);
    }

    /* ───────────── info dialog ───────────── */

    showInfo(handle) {
      const b = this.byHandle[handle];
      if (!b || !this.$dialog) return;
      const row = (label, v) => (v && (!Array.isArray(v) || v.length) ? '<p class="bb__info-row"><strong>' + esc(label) + ':</strong> ' + esc(Array.isArray(v) ? v.join(', ') : v) + '</p>' : '');
      this.$dialogBody.innerHTML =
        (b.image ? '<img class="bb__info-img ' + imgClass(b) + '" src="' + esc(b.image) + '" alt="">' : '') +
        '<h3 class="bb__info-title" id="' + (this.$dialog.getAttribute('aria-labelledby') || '') + '">' + esc(b.name) + '</h3>' +
        (b.benefit ? '<p class="bb__info-benefit">' + esc(b.benefit) + '</p>' : '') +
        // The benefit summary (Spiritual Intent) — moved here from the card.
        (b.summary ? '<p class="bb__info-summary">' + esc(b.summary) + '</p>' : '') +
        (b.description ? '<p class="bb__info-desc">' + esc(b.description) + '</p>' : '') +
        row('Zodiac', b.zodiac) + row('Chakras', b.chakras) + row('Intent', b.intent) +
        (b.affirmation ? '<p class="bb__info-aff">“' + esc(b.affirmation) + '”</p>' : '') +
        (b.available ? '<button type="button" class="btn bb__info-add" data-bb-add-bead="' + esc(b.handle) + '">Add ' + esc(b.name) + '</button>' : '');
      this.$dialog.classList.remove('bb__dialog--wide');
      if (typeof this.$dialog.showModal === 'function') this.$dialog.showModal();
    }

    // The stone's lab report, large: a batch sample, for reference only (not sent with the order).
    showCert(handle) {
      const b = this.byHandle[handle];
      if (!b || !b.cert || !this.$dialog) return;
      const full = b.certFull || b.cert;
      this.$dialogBody.innerHTML =
        '<p class="bb__cert-kicker">Lab test report</p>' +
        '<h3 class="bb__info-title" id="' + (this.$dialog.getAttribute('aria-labelledby') || '') + '">' + esc(b.name) + '</h3>' +
        '<div class="bb__cert-zoom" data-bb-cert-zoom title="Tap to zoom"><img src="' + esc(full) + '" alt="Lab test report: ' + esc(b.name) + '"></div>' +
        '<p class="bb__cert-note">A lab report of a sample from our stone batches, for reference only. Certificates are not included in the order. Tap the report to zoom.</p>' +
        '<a class="bb__cert-full" href="' + esc(full) + '" target="_blank" rel="noopener">Open full size ↗</a>';
      this.$dialog.classList.add('bb__dialog--wide');
      if (typeof this.$dialog.showModal === 'function') this.$dialog.showModal();
    }

    /* ───────────── feedback ───────────── */

    setHint(text) {
      if (this.$hint) this.$hint.textContent = text;
    }

    toast(text) {
      if (!this.$toast) return;
      this.$toast.textContent = text;
      this.$toast.classList.add('is-on');
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => this.$toast.classList.remove('is-on'), 3200);
    }

    /* ───────────── kept in the browser ───────────── */

    storageKey() {
      return 'bb:bracelets:' + (this.config.productHandle || '');
    }

    // The shopper's bracelets, so a reload or a later visit finds them. Bracelets already in
    // the cart are not kept. Written shortly after each change, or at once (now = true).
    persist(now) {
      if (this.workshop) return;
      clearTimeout(this._persistTimer);
      const write = () => {
        try {
          const keep = this.isAdded() ? [] : this.designs.filter((d) => d.slots.some(Boolean));
          if (!keep.length) {
            localStorage.removeItem(this.storageKey());
            return;
          }
          const active = Math.max(0, keep.indexOf(this.designs[this.active]));
          localStorage.setItem(this.storageKey(), JSON.stringify({ v: 1, t: Date.now(), active, designs: keep }));
        } catch (e) {
          // private mode / storage blocked: the page still works, it just won't remember
        }
      };
      if (now) write();
      else this._persistTimer = setTimeout(write, 300);
    }

    // Beads that are gone or out of stock since are left empty, to be chosen again.
    restore() {
      try {
        const raw = localStorage.getItem(this.storageKey());
        if (!raw) return false;
        const data = JSON.parse(raw);
        if (!data || data.v !== 1 || !Array.isArray(data.designs) || !(Date.now() - data.t < KEEP_DAYS * 864e5)) {
          localStorage.removeItem(this.storageKey());
          return false;
        }
        const sizes = [...this.querySelectorAll('[data-bb-size]')];
        const designs = data.designs
          .slice(0, 12)
          .map((d) => {
            const size = sizes.find((b) => b.dataset.variantId === String(d.variantId)) || sizes.find((b) => Number(b.dataset.count) === Number(d.count));
            if (!size) return null;
            const count = Number(size.dataset.count) || this.count;
            const slots = Array.from({ length: count }, (_, i) => {
              const h = Array.isArray(d.slots) ? d.slots[i] : null;
              return h && this.byHandle[h] && this.byHandle[h].available ? h : null;
            });
            return { variantId: size.dataset.variantId, count, slots, designedFor: String(d.designedFor || '').slice(0, 60) };
          })
          .filter((d) => d && d.slots.some(Boolean));
        if (!designs.length) return false;
        this.designs = designs;
        this.load(Math.min(Math.max(0, Number(data.active) || 0), designs.length - 1));
        return true;
      } catch (e) {
        return false;
      }
    }

    /* ───────────── cart ───────────── */

    propertiesFor(d, isActive) {
      const properties = {};
      const nm = this.nameOf(d.slots);
      if (nm) properties.Name = nm;
      properties.Beads = this.beadsText(d.slots);
      const designedFor = isActive && this.filter ? this.filterLabel() : d.designedFor;
      if (designedFor) properties['Designed for'] = designedFor;
      // Staff-only (underscore = hidden from the customer, visible on the admin order page).
      properties['_Bead order'] = this.beadOrderText(d.slots);
      properties['_Design view'] = this.designUrl(d.slots) + '&ref=' + this.newRef();
      properties._pattern = d.slots.join(',');
      properties._stones = this.stonesJson(d.slots);
      properties._builder = 'v1';
      return properties;
    }

    root() {
      return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    }

    async addToCart() {
      if (await this.addReady(this.$adds, 'Adding…')) this.openCart(this.root());
    }

    // Buy it now: the finished bracelets into the cart, then straight to checkout.
    async buyNow() {
      if (this.isAdded() || (await this.addReady([this.$buyNow], 'Going to checkout…'))) {
        this.persist(true); // before leaving the page, so a return visit doesn't re-offer them
        window.location.href = this.root() + 'checkout';
      }
    }

    // Every finished bracelet on the page, in one request (one cart line each).
    async addReady(busy, busyText) {
      this.commit();
      const ready = this.designs.filter(isComplete);
      if (!ready.length) return false;
      const current = this.designs[this.active];
      const items = ready.map((d) => ({ id: Number(d.variantId), quantity: 1, properties: this.propertiesFor(d, d === current) }));

      this.$adds.concat(this.$buyNow || []).forEach((b) => (b.disabled = true));
      busy.forEach((b) => b && (b.textContent = busyText));
      try {
        const res = await fetch(this.root() + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart. Please try again.');
        this.afterAdd(ready.length);
        return true;
      } catch (err) {
        this.toast(err.message);
        return false;
      } finally {
        if (this.$buyNow) this.$buyNow.textContent = 'Buy it now';
        this.renderDesign();
      }
    }

    // The finished bracelets are in the cart. Unfinished ones stay on the page; with none
    // left, the last one stays on the ring and the button offers "Design another".
    afterAdd(n) {
      const current = this.designs[this.active];
      const left = this.designs.filter((d) => !isComplete(d));
      this.history = []; // bracelets now in the cart can't be undone back onto the page
      if (!left.length) {
        this.designs = [current];
        this.load(0);
        this.addedKey = this.designKey();
        return;
      }
      const keep = left.indexOf(current);
      this.designs = left;
      this.load(keep >= 0 ? keep : 0);
      this.addedKey = null;
      this.toast(n + (n === 1 ? ' bracelet' : ' bracelets') + ' added to your cart. ' + (left.length === 1 ? 'One is' : left.length + ' are') + ' not finished yet.');
    }

    // Each design is its own cart line (its own properties, workshop card and bead
    // booking). Once a design is in the cart the button offers the next one until the
    // design changes; a copy of the same bracelet is the cart's quantity.
    designKey() {
      return this.variantId + '|' + this.slots.join(',');
    }

    isAdded() {
      return this.addedKey !== null && this.designs.length === 1 && this.addedKey === this.designKey();
    }

    // Next bracelet after an add: same size and filter, empty ring. Undo brings the last
    // design back (to make a variation of it).
    newDesign() {
      this.snapshot();
      this.slots = new Array(this.count).fill(null);
      this.selected = null;
      this.addedKey = null;
      this.setHint('Tap a stone to start your next bracelet.');
      this.renderDesign();
      this.toast('Your bracelet is in the cart. Now design the next one.');
      this.showRing();
    }

    openCart(root) {
      // The theme's own cart drawer, as on every product page: rebuild it from the cart, then open it.
      if (document.getElementById('CartDrawer')) {
        document.dispatchEvent(new CustomEvent('cart:build'));
        document.dispatchEvent(new CustomEvent('cart:open'));
        return;
      }
      window.location.href = root + 'cart';
    }
  }

  if (!customElements.get('bracelet-builder')) customElements.define('bracelet-builder', BraceletBuilder);
})();
